import { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Alert, CircularProgress, Chip, Stack, Paper, Grid,
  Tooltip, Divider, Link, Table, TableBody, TableCell, TableHead, TableRow,
  Collapse, IconButton, LinearProgress, Tabs, Tab,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ComplianceCatalogTab from '../components/compliance/ComplianceCatalogTab';

// RCCS 준수 현황 (RAY Cloud Compliance Standard) — 제품별로 무엇이 준수(초록)이고
// 무엇이 미준수(빨강)인지 한 화면에. 데이터: GET /api/compliance/status
//
// 🔴 설계 원칙 — unknown 을 pass 로 접지 않는다.
//   "확인 못 함"을 초록으로 칠하면 이 화면은 거짓말을 한다. 3-state 를 그대로 보여주고
//   회색(unknown) 개수가 곧 우리의 관측 가능성 지표다.
//
// 🔴 "고칠 수 있는 것"과 "우리 소관이 아닌 것"을 구분한다.
//   라이선스·권한이 없어 실패한 항목을 담당자에게 빨강으로만 보여주면
//   "내 잘못이 아닌데 빨강" 이 되어 화면을 신뢰하지 않게 된다.

const ST = ['pass', 'fail', 'unknown', 'n/a'];
const ST_LABEL = { pass: '준수', fail: '미준수', unknown: '확인 불가', 'n/a': '해당 없음' };

function stColor(theme, st) {
  const p = theme.palette;
  switch (st) {
    case 'pass': return p.success.main;
    case 'fail': return p.error.main;
    case 'unknown': return p.text.disabled;
    default: return p.action.disabled;
  }
}

// 제품 등급 — 피드에서 계산되어 온다. 여기서는 색·문구만 정한다.
function gradeMeta(theme, g) {
  switch (g) {
    case 'red': return { color: theme.palette.error.main, label: '차단 항목 미준수' };
    case 'amber': return { color: theme.palette.warning.main, label: '미준수 있음' };
    case 'grey': return { color: theme.palette.text.disabled, label: '확인 불가 우세' };
    case 'green': return { color: theme.palette.success.main, label: '준수' };
    default: return { color: theme.palette.text.disabled, label: '판정 없음' };
  }
}

const fmtDate = (v) => (v ? String(v).slice(0, 10) : '—');

// 준수율 — 🔴 분모에서 n/a 만 뺀다. unknown 을 빼면 "모르는 것"이 사라져 비율이 부풀려진다.
function rate(s) {
  const d = (s.pass || 0) + (s.fail || 0) + (s.unknown || 0);
  return d ? Math.round(((s.pass || 0) / d) * 100) : 0;
}

function StatusDot({ status, size = 10 }) {
  const theme = useTheme();
  return (
    <Box component="span" sx={{
      display: 'inline-block', width: size, height: size, borderRadius: '50%',
      bgcolor: stColor(theme, status), flexShrink: 0,
    }} />
  );
}

function StatusBar({ summary }) {
  const theme = useTheme();
  const total = (summary.pass || 0) + (summary.fail || 0) + (summary.unknown || 0);
  if (!total) return null;
  return (
    <Box sx={{ display: 'flex', height: 8, borderRadius: 1, overflow: 'hidden', width: '100%' }}>
      {['pass', 'fail', 'unknown'].map((s) => {
        const v = summary[s] || 0;
        if (!v) return null;
        return (
          <Tooltip key={s} title={`${ST_LABEL[s]} ${v}`}>
            <Box sx={{ width: `${(v / total) * 100}%`, bgcolor: stColor(theme, s) }} />
          </Tooltip>
        );
      })}
    </Box>
  );
}

function ProductRow({ p, expanded, onToggle }) {
  const theme = useTheme();
  const g = gradeMeta(theme, p.grade);
  const s = p.summary || {};
  const t = p.type || {};
  // 🔴 담당자가 지금 손댈 수 있는 것 — 사람 판단·클라우드 권한 대기는 제외된다.
  const actionable = (p.checks || []).filter((c) => c.actionable);

  return (
    <>
      <TableRow hover sx={{ '& > *': { borderBottom: 'unset' }, cursor: 'pointer' }} onClick={onToggle}>
        <TableCell sx={{ width: 40 }}>
          <IconButton size="small">
            {expanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: g.color }} />
            <Typography variant="body2" fontWeight={600}>{p.name || p.product}</Typography>
            {t.profile && <Chip size="small" label={t.profile} variant="outlined" />}
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {p.product}{p.owner ? ` · ${String(p.owner).split('@')[0]}` : ''}
          </Typography>
        </TableCell>
        <TableCell sx={{ width: 130 }}>
          {t.declared ? (
            <Stack direction="row" spacing={0.5}>
              <Chip size="small" label={t.d || '—'} />
              {(t.flags || []).slice(0, 2).map((f) => (
                <Chip key={f} size="small" label={f} variant="outlined" />
              ))}
            </Stack>
          ) : (
            <Tooltip title="Type 미선언 — 최대 범위로 간주됩니다">
              <Chip size="small" color="warning" variant="outlined" label="미선언" />
            </Tooltip>
          )}
        </TableCell>
        <TableCell sx={{ width: 220 }}>
          <StatusBar summary={s} />
          <Typography variant="caption" color="text.secondary">
            준수 {s.pass || 0} · 미준수 {s.fail || 0} · 확인 불가 {s.unknown || 0}
          </Typography>
        </TableCell>
        <TableCell sx={{ width: 90, textAlign: 'right' }}>
          <Typography variant="h6" sx={{ color: g.color, lineHeight: 1.2 }}>{rate(s)}%</Typography>
          {actionable.length > 0 && (
            <Typography variant="caption" color="error">조치 {actionable.length}건</Typography>
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={5} sx={{ py: 0, borderBottom: expanded ? undefined : 'none' }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ py: 2 }}>
              {actionable.length > 0 && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight={600} gutterBottom>
                    지금 조치 가능한 항목 {actionable.length}건
                  </Typography>
                  {actionable.map((c) => (
                    <Typography key={c.id} variant="caption" display="block">
                      · <b>{c.title}</b> — {c.detail}
                    </Typography>
                  ))}
                </Alert>
              )}
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 30 }} />
                    <TableCell>점검 항목</TableCell>
                    <TableCell sx={{ width: 130 }}>통제</TableCell>
                    <TableCell sx={{ width: 110 }}>검사 방법</TableCell>
                    <TableCell>결과</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(p.checks || []).map((c) => (
                    <TableRow key={c.id} sx={{ opacity: c.status === 'n/a' ? 0.45 : 1 }}>
                      <TableCell><StatusDot status={c.status} /></TableCell>
                      <TableCell>
                        <Typography variant="body2">{c.title}</Typography>
                        <Typography variant="caption" color="text.secondary">{c.id}</Typography>
                        {c.gate === 'block' && (
                          <Chip size="small" label="차단" color="error" variant="outlined"
                                sx={{ ml: 0.5, height: 16, fontSize: 10 }} />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {(c.controls || []).join(' · ')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">{c.method}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption"
                                    color={c.status === 'fail' ? 'error' : 'text.secondary'}>
                          {c.detail || ST_LABEL[c.status]}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function CompliancePage() {
  const theme = useTheme();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [open, setOpen] = useState({});
  const [tab, setTab] = useState(0);
  // FEAT-021 — 「우리 기준」 탭. 제품별 현황과 다른 피드이고 갱신 주기도 다르다.
  // 독립 로딩: 한 탭이 실패해도 다른 탭은 정상 동작해야 한다.
  const [catalog, setCatalog] = useState({ data: null, error: null, hint: null, loading: true });

  useEffect(() => {
    fetch('/api/compliance/status')
      .then(async (r) => {
        const j = await r.json().catch(() => null);
        if (!r.ok) throw new Error(j?.error || `HTTP ${r.status}`);
        return j;
      })
      .then(setData)
      .catch((e) => setErr(e.message));

    fetch('/api/compliance/catalog')
      .then(async (r) => {
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw Object.assign(new Error(j.error || `HTTP ${r.status}`), { hint: j.hint });
        return j;
      })
      .then((j) => setCatalog({ data: j, error: null, hint: null, loading: false }))
      .catch((e) => setCatalog({ data: null, error: e.message, hint: e.hint || null, loading: false }));
  }, []);

  const totals = data?.totals;
  const denom = totals ? (totals.pass || 0) + (totals.fail || 0) + (totals.unknown || 0) : 0;
  const overall = denom ? Math.round(((totals.pass || 0) / denom) * 100) : 0;

  // 조항 축 커버리지 — 위의 totals 와 **다른 축**이다.
  //   totals   : 제품 × 점검 항목의 결과 수 (제품이 늘면 같이 는다)
  //   coverage : RCCS 조항 132개 중 몇 개가 확인되고 있는가 (기준 자체의 관측률)
  // 피드에 coverage 가 없는 구버전도 있으므로 없으면 헤더에서 조용히 빠진다.
  const cov = data?.coverage;

  const byCheck = useMemo(
    () => (data?.by_check || []).filter((c) => c.fail > 0).slice(0, 10),
    [data],
  );

  if (err) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          준수 현황을 불러오지 못했습니다 — {err}
        </Alert>
      </Box>
    );
  }
  if (!data) {
    return <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="baseline" spacing={1.5} sx={{ mb: 0.5, flexWrap: 'wrap', rowGap: 0.5 }}>
        <Typography variant="h5" fontWeight={700}>RCS 준수 현황</Typography>
        <Typography variant="caption" color="text.secondary">
          Ray Compliance Standard v{data.standard?.version}
          {data.standard?.manifest_version && (
            /* 기준(RCCS) 버전과 검사 표준 묶음 버전은 서로 다른 것이다. 같이 적어 구분한다. */
            <> · 검사 표준 v{data.standard.manifest_version}</>
          )}
          {' · '}조항 {data.standard?.clauses} · 점검 항목 {data.standard?.items}
        </Typography>

        {/* 🔴 조항 축 커버리지 — "기준의 크기" 옆에 "얼마나 확인되고 있는가" 를 둔다.
            이게 없으면 판정률을 26.5% → 52.3% 로 올려도 화면에 아무 변화가 없다.
            대표 숫자는 pass_confirmed_pct 다: "검증 수단이 있다"(measured) 보다
            "실제로 판정이 나온다" 가 정직하다. 한때 두 값이 68 vs 35 로 두 배
            차이였고, 낙관적인 쪽만 보여주면 준비 상태를 실제보다 좋게 말하게 된다. */}
        {cov && (
          <Tooltip
            arrow
            title={
              <Box sx={{ py: 0.5 }}>
                <Typography variant="caption" display="block" fontWeight={700}>
                  조항 {cov.total}개 기준 커버리지 ({fmtDate(cov.as_of)})
                </Typography>
                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                  🟢 판정 확인 {cov.pass_confirmed} ({cov.pass_confirmed_pct}%) — 실제로 판정이 나오는 조항
                </Typography>
                <Typography variant="caption" display="block">
                  🟡 검증 수단 있음 {cov.measured} ({cov.measured_pct}%) — 수단만 있고 결과는 미확정 포함
                </Typography>
                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                  🔴 미측정 {cov.unmeasured} — 검증 수단이 없어 <b>아무도 확인하지 않는</b> 조항
                </Typography>
                <Divider sx={{ my: 0.75, borderColor: 'rgba(255,255,255,0.25)' }} />
                <Typography variant="caption" display="block">
                  내역: 검증 {cov.verified} · 부분 {cov.partial} · 정의만 {cov.defined} · 미측정 {cov.unmeasured}
                </Typography>
              </Box>
            }
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ cursor: 'help', textDecoration: 'underline dotted', textUnderlineOffset: 3 }}
            >
              판정 {cov.pass_confirmed} ({cov.pass_confirmed_pct}%)
              {' · '}
              <Box component="span" sx={{ color: 'error.main', fontWeight: 700 }}>
                🔴 미측정 {cov.unmeasured}
              </Box>
            </Typography>
          </Tooltip>
        )}
      </Stack>
      <Typography variant="caption" color="text.secondary">
        <Tooltip
          arrow
          title="검사 러너가 UTC 로 돌아 일요일 16:00 UTC = 월요일 01:00 KST 에 실행됩니다. 표시된 기준일은 러너 기준(UTC) 날짜입니다."
        >
          <Box component="span" sx={{ cursor: 'help', textDecoration: 'underline dotted', textUnderlineOffset: 3 }}>
            기준일 {fmtDate(data.generated)}
          </Box>
        </Tooltip>
        {' · '}주 1회 자동 검사 ·{' '}
        <Link href={`https://github.com/${data.sourceRepo}`} target="_blank" rel="noreferrer">
          cloud-compliance <OpenInNewIcon sx={{ fontSize: 11, verticalAlign: 'middle' }} />
        </Link>
      </Typography>

      {/* FEAT-021 — 두 탭은 **다른 질문**에 답한다.
          ① 우리 기준: 무엇을 지켜야 하고 어떻게 확인하는가 (기준이 바뀔 때 갱신)
          ② 제품별 현황: 지금 누가 통과하는가 (매주 갱신)
          🔴 섞으면 "기준이 없는 것" 과 "기준은 있는데 제품이 못 지킨 것" 이 구별되지
             않는다. 조치 대상이 완전히 다르다 — 앞은 기준을 만들어야 하고 뒤는
             제품을 고쳐야 한다. */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ borderBottom: 1, borderColor: 'divider', mt: 2, mb: 2 }}
      >
        <Tab label="제품별 현황" sx={{ minHeight: 44 }} />
        <Tab label="우리 기준" sx={{ minHeight: 44 }} />
      </Tabs>

      {tab === 1 && <ComplianceCatalogTab state={catalog} />}

      {tab === 0 && (
        <>

      {/* 전체 요약 */}
      <Grid container spacing={2} sx={{ mt: 1.5, mb: 2 }}>
        <Grid item xs={6} md={3}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">전체 준수율</Typography>
            <Typography variant="h4" fontWeight={700}>{overall}%</Typography>
            <LinearProgress variant="determinate" value={overall} sx={{ mt: 1, height: 6, borderRadius: 3 }} />
            <Typography variant="caption" color="text.secondary">
              제품 {totals?.products} · 항목 {totals?.checks_per_product}
            </Typography>
          </Paper>
        </Grid>
        {[
          ['pass', '준수', totals?.pass],
          ['fail', '미준수', totals?.fail],
          ['unknown', '확인 불가', totals?.unknown],
        ].map(([k, label, v]) => (
          <Grid item xs={6} md={3} key={k}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <StatusDot status={k} size={9} />
                <Typography variant="caption" color="text.secondary">{label}</Typography>
              </Stack>
              <Typography variant="h4" fontWeight={700} sx={{ color: stColor(theme, k) }}>{v ?? 0}</Typography>
              {k === 'unknown' && (
                <Typography variant="caption" color="text.secondary">
                  🔴 준수가 아닙니다 — 확인 수단이 없는 것
                </Typography>
              )}
              {k === 'fail' && totals?.block_fail > 0 && (
                <Typography variant="caption" color="error">차단 게이트 {totals.block_fail}건</Typography>
              )}
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* 🔴 전 제품 공통 실패 — 개별 제품 문제가 아니다 */}
      {(data.common_failures || []).length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight={600} gutterBottom>
            전 제품 공통 미준수 {data.common_failures.length}건 — 개별 제품 문제가 아닙니다
          </Typography>
          {data.common_failures.map((c) => (
            <Typography key={c.id} variant="caption" display="block">
              · <b>{c.title}</b> ({c.id}) — {c.products}개 제품 전부
            </Typography>
          ))}
        </Alert>
      )}

      {/* 제품별 */}
      <Paper variant="outlined" sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 40 }} />
              <TableCell>제품</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>점검 결과</TableCell>
              <TableCell sx={{ textAlign: 'right' }}>준수율</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(data.products || []).map((p) => (
              <ProductRow key={p.product} p={p}
                          expanded={!!open[p.product]}
                          onToggle={() => setOpen((o) => ({ ...o, [p.product]: !o[p.product] }))} />
            ))}
          </TableBody>
        </Table>
      </Paper>

      {/* 항목별 — 어느 항목이 가장 많이 실패하는가 */}
      {byCheck.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
            미준수가 많은 항목
          </Typography>
          <Divider sx={{ mb: 1 }} />
          <Table size="small">
            <TableBody>
              {byCheck.map((c) => (
                <TableRow key={c.id}>
                  <TableCell sx={{ width: '45%' }}>
                    <Typography variant="body2">{c.title}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {c.id} · {(c.controls || []).join(' · ')}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ width: 100 }}>
                    <Typography variant="caption" color="text.secondary">{c.method}</Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <Chip size="small" color="error" variant="outlined" label={`미준수 ${c.fail}`} />
                      {c.pass > 0 && <Chip size="small" color="success" variant="outlined" label={`준수 ${c.pass}`} />}
                      {c.unknown > 0 && <Chip size="small" variant="outlined" label={`확인 불가 ${c.unknown}`} />}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {(c.failing_products || []).slice(0, 3).join(', ')}
                      {(c.failing_products || []).length > 3 ? ` 외 ${c.failing_products.length - 3}` : ''}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
        ⚠️ <b>확인 불가(회색)는 준수가 아닙니다.</b> 검사 수단이 없거나 권한이 없어 판정하지 못한 항목이며,
        이 숫자가 곧 우리가 무엇을 모르는지를 나타냅니다.
      </Typography>        </>
      )}

    </Box>
  );
}
